import { Injectable } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import {
    AccountsClient,
    AssociatedCompanyModelDto,
    ChallengeGroupsClient,
    CompaniesClient,
    CompanyAccountSettingsModelDto,
    CompanyModelDto,
    LookupModelDto,
    SecureChallengeClient,
} from '@treasury/api/bo';
import { CompanyUserSummary } from '../../../company/user/entities';
import { Account } from '../account/entities/account.entity';
import { ChallengeGroup, Company } from './';
import * as ProductFeatures from './entities';
import { CompanyAccountSettings } from './entities/company-account-settings.entity';
import { CompanyAccount } from './entities/company-account.entity';
import { CompanyUserApprovalSettings } from './entities/company-user-approval-settings.entity';

@Injectable()
export class CompanyService {
    constructor(
        private readonly companiesClient: CompaniesClient,
        private readonly challengeGroupsClient: ChallengeGroupsClient,
        private readonly secureChallengeClient: SecureChallengeClient,
        private readonly accountsClient: AccountsClient
    ) {}

    /**
     * Retrieves a list of companies by the current FI.
     * @returns A promise that resolves to an array of companies.
     */
    public async getCompaniesByCurrentFI() {
        const companies = await this.companiesClient.companiesGetCompaniesByFi();
        return companies.data.map(company => new Company(company));
    }

    /**
     * Retrieves a company by its ID and type.
     * @param companyId - The ID of the company.
     * @param companyType - The type of the company.
     * @returns A Promise that resolves to a Company object.
     */
    public async getCompanyById(companyId: number, companyType: number) {
        const companyResponse = await this.companiesClient.companiesGetOnboardedCompany(
            companyId,
            companyType
        );

        if (!companyResponse.data) {
            throw new Error(`Company ${companyId} not found`);
        }

        const challengeGroups = await this.getAllChallengeGroups();

        return new Company(companyResponse.data, challengeGroups);
    }

    /**
     * Updates the unique identifier of a company.
     *
     * @param companyId - The ID of the company to update.
     * @param companyUniqueId - The new unique identifier for the company.
     * @returns A promise that resolves to an instance of the updated Company.
     */
    public async updateCompanyUniqueId(companyId: number, companyUniqueId: string) {
        const response = await this.companiesClient.companiesUpdateCompanyUniqueId(
            companyId,
            companyUniqueId
        );

        return new Company(response.data);
    }

    public async updateCompanyAuthProfile(companyId: number, authProfileId: number) {
        const response = await this.secureChallengeClient.secureChallengeUpdateChallengeGroup({
            companyId,
            challengeGroupId: authProfileId,
        });

        return response.data;
    }

    /**
     * Updates the authentication status of a company.
     *
     * @param companyId - The unique identifier of the company.
     * @param authEnabled - A boolean indicating whether authentication is enabled or not.
     * @returns A promise that resolves to the response data from the secure challenge client.
     */
    public async updateCompanyAuthStatus(companyId: number, authEnabled: boolean) {
        const response = await this.secureChallengeClient.secureChallengeUpdateStatus({
            companyId,
            isSecureChallengeEnabled: authEnabled,
        });
        return response.data;
    }

    /**
     * Updates the authentication method for a specified company.
     *
     * @param companyId - The unique identifier of the company.
     * @param authMethod - The new authentication method to be set for the company.
     * @returns A promise that resolves to the response data from the secure challenge client.
     */
    public async updateCompanyAuthMethod(companyId: number, authMethod: number) {
        const response = await this.secureChallengeClient.secureChallengeUpdateChallengeMethodType({
            companyId,
            challengeMethodType: authMethod,
        });
        return response.data;
    }

    /**
     * Retrieves all challenge groups.
     */
    public async getAllChallengeGroups() {
        const challengeGroups = await this.challengeGroupsClient.challengeGroupsGetAll();
        return challengeGroups.data.map(cg => new ChallengeGroup(cg));
    }

    public async getActiveCompanyAccounts(companyId: number) {
        return (await this.getCompanyAccounts(companyId)).filter(
            account =>
                account.status === 'Active' &&
                !['', 'Dormant', 'Closed', 'Escheat'].includes(account.coreStatus ?? '')
        );
    }

    public async getCompanyAccounts(companyId: number) {
        const companyIdString = companyId.toString();
        const accounts = (await this.companiesClient.companiesAccountsAllGet(companyIdString)).data;
        return accounts.map(account => new CompanyAccount(account));
    }

    public async getCompanyAccount(companyId: number, accountId: number): Promise<CompanyAccount> {
        const account = (await this.companiesClient.companiesAccountsGet(companyId, accountId))
            .data;
        return new CompanyAccount(account);
    }

    public async getUsers(companyId: number) {
        const users = (await this.companiesClient.companiesUsersAll(companyId, false)).data;
        return users.map(user => new CompanyUserSummary(user));
    }

    public async updateCompanyStatus(company: Company) {
        const dto = company.toDto();
        const companyIdString = dto.id.toString();
        const response = await this.companiesClient.companiesStatus(companyIdString, dto);

        return response.data;
    }

    public async updateCompanyAccountStatus(companyId: number, accountId: number, active: boolean) {
        return this.companiesClient.companiesAccountStatus(
            companyId,
            accountId,
            active ? 'Active' : 'Inactive'
        );
    }

    /**
     * Retrieves the user approval settings for a specific company.
     *
     * @param companyId - The unique identifier of the company.
     * @returns A promise that resolves to an instance of `CompanyUserApprovalSettings`
     *          containing the approval settings for the specified company.
     * @throws Will throw an error if the request to fetch the approval settings fails.
     */
    public async getCompanyUserApprovalSettings(companyId: number) {
        const response = await this.companiesClient.companiesApprovalSettingsGet(companyId);
        return new CompanyUserApprovalSettings(response.data);
    }

    /**
     * Saves the approval settings for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param companyUserApprovalSettings - The approval settings to be saved for the company user.
     * @returns A promise that resolves to the updated `CompanyUserApprovalSettings` instance.
     */
    public async saveCompanyUserApprovalSettings(
        companyId: number,
        companyUserApprovalSettings: CompanyUserApprovalSettings
    ) {
        const response = await this.companiesClient.companiesApprovalSettingsPut(
            companyId,
            companyUserApprovalSettings.id,
            companyUserApprovalSettings.toDto()
        );
        return new CompanyUserApprovalSettings(response.data);
    }

    public async getCompanyUserAccountSettings(companyId: number): Promise<CompanyAccountSettings> {
        return new CompanyAccountSettings(
            (await this.companiesClient.companiesUserAccountSettingsGet(companyId)).data
        );
    }

    public async saveCompanyUserAccountSettings(
        companyId: number,
        dto: CompanyAccountSettingsModelDto
    ): Promise<CompanyAccountSettings> {
        return new CompanyAccountSettings(
            (await this.companiesClient.companiesUserAccountSettingsPut(companyId, dto)).data
        );
    }

    public async getAssociatedCompanies(
        companyId: number
    ): Promise<ProductFeatures.AssociatedCompany[]> {
        return (await this.companiesClient.companiesAssociatedCompaniesAll(companyId)).data.map(
            (x: AssociatedCompanyModelDto) => new ProductFeatures.AssociatedCompany(x)
        );
    }

    public async searchNonOnboardedCompanies(searchValue: string): Promise<Company[]> {
        const companyNameSearch = async () =>
            await this.companiesClient.companiesNonOnboardedCompanies({
                searchType: 'Company Name',
                searchValue,
            });

        const taxIdNumberSearch = /^\d+$/.test(searchValue)
            ? async () =>
                  await this.companiesClient.companiesNonOnboardedCompanies({
                      searchType: 'TAX ID Number',
                      searchValue,
                  })
            : () => Promise.resolve({ data: [] as CompanyModelDto[] });

        const cifNumberSearch = async () =>
            await this.companiesClient.companiesNonOnboardedCompanies({
                searchType: 'CIF Number',
                searchValue,
            });

        return (await Promise.all([cifNumberSearch(), companyNameSearch(), taxIdNumberSearch()]))
            .flatMap(x => x.data)
            .map(x => new Company(x));
    }

    public async addAssociatedCompany(companyId: number, cifNumber: string) {
        return this.companiesClient.companiesAssociatedCompanies(companyId, {
            value: cifNumber,
        } as unknown as LookupModelDto);
    }

    public async getRelatedAccounts(companyId: number): Promise<Account[]> {
        return (
            await this.accountsClient.accountsGetAccountsByCompanyId(companyId.toString(), {
                accountType: undefined,
                includeHoldingCompanies: true,
            })
        ).data.map(account => new Account(account));
    }

    public async addCompanyAccounts(
        companyId: number,
        accounts: Account[],
        getNewAccounts: false
    ): Promise<void>;

    public async addCompanyAccounts(
        companyId: number,
        accounts: Account[],
        getNewAccounts: true
    ): Promise<CompanyAccount[]>;

    public async addCompanyAccounts(companyId: number, accounts: Account[]): Promise<void>;

    /**
     * Add a list of accounts to a company.
     *
     * @param companyId The company to add the accounts to.
     * @param getNewAccounts If `true`, returns the provided accounts as
     * new company accounts.
     *
     * **Note**: This incurs a second API call.
     *
     * @returns The provided accounts as company accounts if `getNewAccounts` was `true`.
     * Otherwise, nothing.
     */
    public async addCompanyAccounts(
        companyId: number,
        accounts: Account[],
        getNewAccounts = false
    ): Promise<CompanyAccount[] | void> {
        const accountsToAdd: LookupModelDto[] = accounts
            .filter(accountToAdd => exists(accountToAdd.cifNumber))
            .map(account => ({
                key: account.cifNumber,
                value: `${account.accountNumber}|${account.accountType}`,
            }));

        // the data that comes back appears to be all of the companies
        // *except* for what was sent in the payload
        await this.companiesClient.companiesAccountsAllPost(
            companyId.toString(),
            { id: 0, lookups: accountsToAdd },
            {
                activate: false,
            }
        );

        // if we want to return the new companies, we need to get the full list and do a diff
        if (getNewAccounts) {
            const allAccounts = await this.getCompanyAccounts(companyId);
            return accounts
                .map(newAccount => {
                    const { cifNumber, accountNumber, accountType } = newAccount;
                    return allAccounts.find(
                        a =>
                            a.cifNumber === cifNumber &&
                            a.number === accountNumber &&
                            a.type === accountType
                    );
                })
                .filter(exists);
        }

        return undefined;
    }

    public async searchUnrelatedAccounts(
        companyId: number,
        searchQuery: string
    ): Promise<Account[]> {
        const checkingResults = this.accountsClient.accountsSearchUnrelatedAccounts(companyId, {
            accountNumber: searchQuery,
            accountType: 'Checking',
        });
        const savingsResults = this.accountsClient.accountsSearchUnrelatedAccounts(companyId, {
            accountNumber: searchQuery,
            accountType: 'Savings',
        });
        const loanResults = this.accountsClient.accountsSearchUnrelatedAccounts(companyId, {
            accountNumber: searchQuery,
            accountType: 'Loan',
        });
        const timeDepositResults = this.accountsClient.accountsSearchUnrelatedAccounts(companyId, {
            accountNumber: searchQuery,
            accountType: 'Time Deposit',
        });
        const cifResults = this.accountsClient.accountsSearchUnrelatedAccounts(companyId, {
            cif: searchQuery,
        });

        return (
            await Promise.all([
                checkingResults,
                savingsResults,
                loanResults,
                timeDepositResults,
                cifResults,
            ])
        )
            .flatMap(results => results.data)
            .flatMap(datum => datum.accounts ?? [])
            .map(account => new Account(account));
    }
}
